import { useEffect, useMemo, useRef, useState } from "react";
import { Alert } from "react-bootstrap";
import {
  organizerCreateGiftTicketsApi,
  organizerListEventsApi,
  organizerListGiftTicketsApi,
  organizerPhysicalListEventsApi,
  organizerPhysicalListTicketTypesApi,
  organizerTicketTypesByEventApi,
} from "../../api/api";
import { handleError } from "../../helpers/handleError";
import { parseExcelBuffer, downloadTemplate } from './excelHelpers';
import { IconClock, IconCalendarPlain, IconTrash } from "./giftIcons";
import { isFullName, parseTimeValue, useSquareIcon } from "./giftHelpers";
import GiftTicketCard from "./GiftTicketCard";
import SpinLoading from "../../components/spinLoading/SpinLoading";
import OrganizerPillSelect from "../components/OrganizerPillSelect";
import { useSelector } from "react-redux";
import zocoLogo from "../../assets/z.png";
import "../../pages/myevents/detail.css";
import "../dashboard.css";
import "../components/ZocoToggle.css";
import "./Gifts.css";

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

const GiftTickets = () => {
  const auth = useSelector((state) => state.auth);
  const role = String(auth?.user?.role || "").toUpperCase();
  const isSeller = role === "SELLER";
  const [events, setEvents] = useState([]);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [eventId, setEventId] = useState("");
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    ticketTypeId: "",
    qty: 1,
    recipientName: "",
    expiryMode: "free",
    expiresAt: "",
  });

  const [bulkRows, setBulkRows] = useState([]);
  const [bulkStatus, setBulkStatus] = useState('idle');
  const [bulkResults, setBulkResults] = useState([]);
  const bulkFileRef = useRef(null);

  const [timeOpen, setTimeOpen] = useState(false);
  const timePickerRef = useRef(null);

  const [existingTicketKeys, setExistingTicketKeys] = useState(new Set());
  const [lastEventId, setLastEventId] = useState("");
  const [showHistory, setShowHistory] = useState(false);

  const [currentHour, currentMinute] = useMemo(() => {
    const raw = form.expiresAt || "";
    const parts = raw.split(":");
    return [parts[0] || "", parts[1] || ""];
  }, [form.expiresAt]);

  const handleSelectHour = (h) => {
    const nextMinute = currentMinute || "00";
    setForm((prev) => ({ ...prev, expiresAt: `${h}:${nextMinute}` }));
  };

  const handleSelectMinute = (m) => {
    const nextHour = currentHour || "12";
    setForm((prev) => ({ ...prev, expiresAt: `${nextHour}:${m}` }));
  };

  const hasTicketTypes = ticketTypes.length > 0;

  const loadEvents = async () => {
    try {
      setLoading(true);
      const res = isSeller ? await organizerPhysicalListEventsApi() : await organizerListEventsApi();
      const list = Array.isArray(res?.data?.info) ? res.data.info : [];
      setEvents(list);
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  const loadTicketTypes = async (selectedEventId) => {
    if (!selectedEventId) {
      setTicketTypes([]);
      return;
    }
    try {
      const res = isSeller
        ? await organizerPhysicalListTicketTypesApi(selectedEventId)
        : await organizerTicketTypesByEventApi({ eventid: selectedEventId });
      const list = Array.isArray(res?.data?.info) ? res.data.info : [];
      setTicketTypes(list);
    } catch (err) {
      handleError(err);
      setTicketTypes([]);
    }
  };

  const loadGifts = async (selectedEventId) => {
    if (!selectedEventId) {
      setGifts([]);
      return;
    }
    try {
      const res = await organizerListGiftTicketsApi({ eventId: selectedEventId });
      const list = Array.isArray(res?.data?.info) ? res.data.info : [];
      setGifts(list);

      if (selectedEventId !== lastEventId) {
        const keys = new Set();
        list.forEach((order) => {
          (order.tickets || []).forEach((ticket) => {
            keys.add(`${order.orderId}-${ticket.ticketId || ticket.seq}`);
          });
        });
        setExistingTicketKeys(keys);
        setLastEventId(selectedEventId);
        setShowHistory(false);
      }
    } catch (err) {
      handleError(err);
      setGifts([]);
    }
  };

  useEffect(() => {
    loadEvents();
  }, [isSeller]);

  useEffect(() => {
    if (!eventId) {
      setTicketTypes([]);
      setGifts([]);
      return;
    }
    loadTicketTypes(eventId);
    loadGifts(eventId);
  }, [eventId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!timePickerRef.current) return;
      if (!timePickerRef.current.contains(event.target)) setTimeOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (timeOpen) {
      setTimeout(() => {
        if (timePickerRef.current) {
          const activeButtons = timePickerRef.current.querySelectorAll(".zoco-time-option.is-active");
          activeButtons.forEach((btn) => {
            btn.scrollIntoView({ block: "center", behavior: "auto" });
          });
        }
      }, 50);
    }
  }, [timeOpen]);

  const eventOptions = useMemo(
    () =>
      events
        .map((ev) => {
          const id = ev?.id ?? ev?._id ?? ev?.eventid;
          if (!id) return null;
          return { value: String(id), label: ev?.name || "Evento sin nombre" };
        })
        .filter(Boolean),
    [events]
  );

  useEffect(() => {
    if (!isSeller || eventId || eventOptions.length !== 1) return;
    setEventId(String(eventOptions[0].value || ""));
  }, [isSeller, eventId, eventOptions]);

  const ticketTypeOptions = useMemo(() => {
    if (!ticketTypes.length) return [];
    return ticketTypes.map((t) => ({
      value: String(t._id || t.id || t.ticketTypeId),
      label: t.name ? `${t.name}${Number(t.price) ? ` ($${t.price})` : ""}` : "Entrada",
    }));
  }, [ticketTypes]);

  const flatTickets = useMemo(() => {
    const rows = [];
    gifts.forEach((order) => {
      (order.tickets || []).forEach((ticket) => {
        rows.push({ order, ticket, key: `${order.orderId}-${ticket.ticketId || ticket.seq}` });
      });
    });
    return rows;
  }, [gifts]);

  const { recentTickets, historyTickets } = useMemo(() => {
    const recent = [];
    const history = [];
    flatTickets.forEach((item) => {
      if (existingTicketKeys.has(item.key)) {
        history.push(item);
      } else {
        recent.push(item);
      }
    });
    return { recentTickets: recent, historyTickets: history };
  }, [flatTickets, existingTicketKeys]);

  const qrIcon = useSquareIcon(zocoLogo, 96);

  const onCreate = async (e) => {
    e.preventDefault();
    setError("");
    if (!eventId) {
      setError("Selecciona un evento para continuar.");
      return;
    }
    if (hasTicketTypes && !form.ticketTypeId) {
      setError("Selecciona un tipo de entrada.");
      return;
    }
    if (!isFullName(form.recipientName)) {
      setError("Ingresá nombre y apellido del destinatario.");
      return;
    }
    let expiresAtValue = null;
    if (form.expiryMode === "date") {
      const parsed = parseTimeValue(form.expiresAt);
      if (!parsed) {
        setError("Ingresa una hora de vencimiento (HH:mm).");
        return;
      }
      expiresAtValue = parsed;
    }
    try {
      setSubmitting(true);
      const payload = {
        eventId,
        ticketTypeId: form.ticketTypeId || null,
        qty: Number(form.qty || 1),
        recipientName: String(form.recipientName || "").trim(),
      };
      if (expiresAtValue) payload.expiresAt = expiresAtValue;
      await organizerCreateGiftTicketsApi(payload);
      setForm((prev) => ({ ...prev, qty: 1, recipientName: "" }));
      await loadGifts(eventId);
    } catch (err) {
      const message = err?.response?.data?.message || "No se pudieron generar las entradas.";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const resetBulk = () => {
    setBulkRows([]);
    setBulkStatus('idle');
    setBulkResults([]);
    if (bulkFileRef.current) bulkFileRef.current.value = '';
  };

  const onFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    const reader = new FileReader();
    reader.onload = (ev) => {
      const buffer = ev.target?.result;
      if (!buffer) return;
      const { rows, error: parseError } = parseExcelBuffer(buffer);
      if (parseError) {
        setError(parseError);
        if (bulkFileRef.current) bulkFileRef.current.value = '';
        return;
      }
      setBulkRows(rows);
      setBulkResults([]);
      setBulkStatus('preview');
    };
    reader.readAsArrayBuffer(file);
  };

  const runBulkImport = async () => {
    if (!bulkRows.length) return;
    if (!eventId) {
      setError('Seleccioná un evento antes de importar el Excel.');
      return;
    }
    if (hasTicketTypes && !form.ticketTypeId) {
      setError('Seleccioná un tipo de entrada.');
      return;
    }

    let expiresAtValue = null;
    if (form.expiryMode === 'date') {
      expiresAtValue = parseTimeValue(form.expiresAt);
      if (!expiresAtValue) {
        setError('Ingresá una hora de vencimiento (HH:mm).');
        return;
      }
    }

    setError('');
    setBulkStatus('processing');
    setBulkResults([]);

    const results = [];
    for (let i = 0; i < bulkRows.length; i += 1) {
      const row = bulkRows[i];
      const payload = {
        eventId,
        ticketTypeId: form.ticketTypeId || null,
        qty: 1,
        recipientName: `${row.Nombre} ${row.Apellido}`.trim(),
        recipientEmail: row['Correo electronico'],
        ...(expiresAtValue ? { expiresAt: expiresAtValue } : {}),
      };
      try {
        await organizerCreateGiftTicketsApi(payload);
        results.push({ index: i, ok: true });
      } catch (err) {
        const message = err?.response?.data?.message || 'Error al generar la entrada.';
        results.push({ index: i, ok: false, error: message });
      }
      setBulkResults([...results]);
    }

    setBulkStatus('done');
    await loadGifts(eventId);
  };

  if (loading) return <SpinLoading />;

  const vencimientoOn = form.expiryMode === "date";
  const toggleVencimiento = () => {
    setForm((prev) => {
      const nextMode = prev.expiryMode === "date" ? "free" : "date";
      return {
        ...prev,
        expiryMode: nextMode,
        expiresAt: nextMode === "free" ? "" : prev.expiresAt,
      };
    });
  };

  const okCount = bulkResults.filter((r) => r.ok).length;
  const errCount = bulkResults.filter((r) => !r.ok).length;

  return (
    <div className="organizer-page gifts-especiales">
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      {/* ---- Generar nuevas entradas ---- */}
      <section className="zoco-card">
        <h2 className="zoco-card-title">Generar nuevas entradas</h2>
        <form onSubmit={onCreate}>
          {/* Evento — ancho completo */}
          <div className="zoco-form-top">
            <OrganizerPillSelect
              placeholder="Seleccioná un evento"
              value={eventId}
              onChange={(val) => {
                setEventId(val ? String(val) : "");
                setForm((prev) => ({ ...prev, ticketTypeId: "" }));
              }}
              options={eventOptions}
            />
          </div>

          {/* Grilla 2 columnas */}
          <div className="zoco-form-grid">
            {/* Tipo de entrada */}
            <div className="zoco-field">
              <span className="zoco-label">Tipo de entrada</span>
              <OrganizerPillSelect
                placeholder="General"
                value={form.ticketTypeId}
                disabled={!hasTicketTypes}
                onChange={(val) => setForm((prev) => ({ ...prev, ticketTypeId: val ? String(val) : "" }))}
                options={ticketTypeOptions}
              />
            </div>

            {/* Horario de Vencimiento (título + toggle) */}
            <div className="zoco-venc-head">
              <div>
                <div className="zoco-venc-title">Horario de Vencimiento</div>
                <div className="zoco-venc-sub">| Define si las entradas tendrán tiempo límite</div>
              </div>
              <button
                type="button"
                className={"zoco-toggle" + (vencimientoOn ? " is-on" : "")}
                role="switch"
                aria-checked={vencimientoOn}
                aria-label="Horario de vencimiento"
                onClick={toggleVencimiento}
              >
                <span className="knob" />
              </button>
            </div>

            {/* Cantidad */}
            <div className="zoco-field">
              <span className="zoco-label">Cantidad</span>
              <div className="zoco-control">
                <input
                  type="number"
                  min={1}
                  max={200}
                  placeholder="1"
                  value={form.qty}
                  onChange={(e) => setForm((prev) => ({ ...prev, qty: e.target.value }))}
                  required
                />
              </div>
            </div>

            {/* Hora de vencimiento */}
            <div className="zoco-field">
              <span className="zoco-label">Hora de vencimiento (24 hs)</span>
              <div ref={timePickerRef} className="zoco-time-picker-wrap">
                <button
                  type="button"
                  className={"zoco-control is-select" + (vencimientoOn ? "" : " is-muted") + (form.expiresAt ? " has-value" : "")}
                  disabled={!vencimientoOn}
                  onClick={() => vencimientoOn && setTimeOpen((v) => !v)}
                  aria-expanded={timeOpen}
                >
                  <span>{form.expiresAt || "--:--"}</span>
                  <span className="zoco-clock"><IconClock size={16} /></span>
                </button>
                {vencimientoOn && timeOpen && (
                  <div className="zoco-time-pill-panel">
                    <div className="zoco-time-columns">
                      <div className="zoco-time-column">
                        <div className="zoco-time-scroll">
                          {HOURS.map((h) => (
                            <button
                              key={h}
                              type="button"
                              className={"zoco-time-option" + (currentHour === h ? " is-active" : "")}
                              onClick={() => handleSelectHour(h)}
                            >
                              {h}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="zoco-time-column">
                        <div className="zoco-time-scroll">
                          {MINUTES.map((m) => (
                            <button
                              key={m}
                              type="button"
                              className={"zoco-time-option" + (currentMinute === m ? " is-active" : "")}
                              onClick={() => handleSelectMinute(m)}
                            >
                              {m}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Nombre y Apellido — ancho completo */}
          <div className="zoco-form-full">
            <div className="zoco-field">
              <span className="zoco-label">Nombre y Apellido</span>
              <div className="zoco-control">
                <input
                  type="text"
                  placeholder="Ej: Juan Perez"
                  value={form.recipientName}
                  onChange={(e) => setForm((prev) => ({ ...prev, recipientName: e.target.value }))}
                  required
                />
              </div>
            </div>
          </div>

          {/* Botonera */}
          <div className="zoco-form-actions">
            <button
              type="button"
              className="zoco-btn zoco-btn-light"
              disabled={submitting || bulkStatus === "processing"}
              onClick={() => bulkFileRef.current?.click()}
            >
              Subir Excel
            </button>
            <button
              type="button"
              className="zoco-btn zoco-btn-light"
              disabled={bulkStatus === "processing"}
              onClick={downloadTemplate}
            >
              Descargar plantilla
            </button>
            <button
              type="submit"
              className="zoco-btn zoco-btn-dark"
              disabled={submitting || bulkStatus === "processing"}
            >
              {submitting ? "Generando..." : "Generar entradas"}
            </button>
            <input
              ref={bulkFileRef}
              type="file"
              accept=".xlsx,.csv"
              style={{ display: "none" }}
              onChange={onFileSelect}
            />
          </div>
        </form>
      </section>

      {/* ---- Excel · Vista previa ---- */}
      {bulkStatus !== "idle" && (
        <section className="zoco-card">
          <div className="zoco-excel-head">
            <div>
              <h2 className="zoco-excel-title"><strong>Excel</strong> | Vista previa</h2>
              <p className="zoco-card-subtitle">
                {bulkStatus === "processing"
                  ? `| Procesando ${Math.min(bulkResults.length + 1, bulkRows.length)} de ${bulkRows.length}…`
                  : bulkStatus === "done"
                  ? "| Proceso finalizado"
                  : <>| <strong>{bulkRows.length}</strong> Personas</>}
              </p>
            </div>
            {bulkStatus !== "processing" && (
              <button
                type="button"
                className="zoco-trash"
                aria-label={bulkStatus === "done" ? "Cerrar" : "Cancelar"}
                title={bulkStatus === "done" ? "Cerrar" : "Cancelar"}
                onClick={resetBulk}
              >
                <IconTrash size={22} />
              </button>
            )}
          </div>

          <div className="zoco-table">
            <div className="zoco-table-scroll">
              <div className="zoco-table-row head">
                <span>#</span>
                <span>NOMBRE</span>
                <span>APELLIDO</span>
                <span>CORREO</span>
                <span className="cell-right">ESTADO</span>
              </div>
              {bulkRows.map((row, i) => {
                const result = bulkResults.find((r) => r.index === i);
                let badgeClass = "pendiente";
                let badgeLabel = "Pendiente";
                let badgeTitle;
                if (bulkStatus === "processing" && !result && i === bulkResults.length) {
                  badgeClass = "procesando";
                  badgeLabel = "Procesando…";
                } else if (result?.ok) {
                  badgeClass = "generada";
                  badgeLabel = "Generada";
                } else if (result && !result.ok) {
                  badgeClass = "error";
                  badgeLabel = "Error";
                  badgeTitle = result.error;
                }
                return (
                  <div className="zoco-table-row body" key={`${row.Nombre}-${row.Apellido}-${i}`}>
                    <span className="cell">{i + 1}</span>
                    <span className="cell">{row.Nombre}</span>
                    <span className="cell">{row.Apellido}</span>
                    <span className="cell">{row["Correo electronico"]}</span>
                    <span className="cell-right">
                      <span className={`zoco-badge ${badgeClass}`} title={badgeTitle}>
                        <span className="dot" />
                        {badgeLabel}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {bulkStatus === "preview" && (
            <div className="zoco-excel-actions">
              <button type="button" className="zoco-btn-confirm" onClick={runBulkImport}>
                Confirmar y Generar {bulkRows.length} entradas
              </button>
            </div>
          )}

          {bulkStatus === "done" && (
            <p className="zoco-excel-summary">
              <strong>{okCount}</strong> entradas generadas correctamente.{" "}
              {errCount > 0 && (
                <span className="err"><strong>{errCount}</strong> con error.</span>
              )}
            </p>
          )}
        </section>
      )}

      {/* ---- Entradas generadas ---- */}
      <section className="zoco-card">
        <div className="zoco-card-head">
          <div>
            <h2 className="zoco-card-title">Entradas generadas</h2>
            <p className="zoco-card-subtitle">| Descargá el QR con logo y compartilo con quien quieras.</p>
          </div>
          <span className="zoco-card-count"><strong>{flatTickets.length}</strong> Entradas</span>
        </div>

        {eventId ? (
          flatTickets.length ? (
            <div className="zoco-sections-container">
              {/* SUBSECCION 1: GENERADAS EN ESTE MOMENTO */}
              <div className="zoco-subsection">
                <h3 className="zoco-subsection-title">Generadas en este momento</h3>
                {recentTickets.length ? (
                  <div className="zoco-qr-grid">
                    {recentTickets.map(({ order, ticket, key }) => (
                      <GiftTicketCard
                        key={key}
                        order={order}
                        ticket={ticket}
                        qrIcon={qrIcon}
                        onChanged={() => loadGifts(eventId)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="zoco-empty-mini">
                    Ninguna entrada generada en esta sesión todavía.
                  </div>
                )}
              </div>

              {/* SUBSECCION 2: HISTORIAL DE ENTRADAS ANTERIORES */}
              <div className="zoco-subsection zoco-history-section">
                <div className="zoco-history-head-row">
                  <h3 className="zoco-subsection-title">Historial de entradas anteriores</h3>
                  <div className="zoco-history-toggle-wrap">
                    <button
                      type="button"
                      className={"zoco-toggle" + (showHistory ? " is-on" : "")}
                      role="switch"
                      aria-checked={showHistory}
                      aria-label="Ver historial"
                      onClick={() => setShowHistory((v) => !v)}
                    >
                      <span className="knob" />
                    </button>
                  </div>
                </div>

                {showHistory && (
                  historyTickets.length ? (
                    <div className="zoco-qr-grid">
                      {historyTickets.map(({ order, ticket, key }) => (
                        <GiftTicketCard
                          key={key}
                          order={order}
                          ticket={ticket}
                          qrIcon={qrIcon}
                          onChanged={() => loadGifts(eventId)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="zoco-empty-mini">
                      No hay entradas anteriores en el historial.
                    </div>
                  )
                )}
              </div>
            </div>
          ) : (
            <div className="zoco-empty">
              <span className="zoco-empty-icon"><IconCalendarPlain size={26} /></span>
              <div className="zoco-empty-strong">Todavía no generaste entradas</div>
              <div className="zoco-empty-sub">para este evento</div>
            </div>
          )
        ) : (
          <div className="zoco-empty">
            <span className="zoco-empty-icon"><IconCalendarPlain size={26} /></span>
            <div className="zoco-empty-strong">Seleccioná un evento</div>
            <div className="zoco-empty-sub">para ver las entradas</div>
          </div>
        )}
      </section>
    </div>
  );
};

export default GiftTickets;
